import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import advertisementAPI, { Advertisement, AdvertisementFilters, AdvertisementStatistics } from '../../services/advertisementAPI';
import AdvertisementForm from './AdvertisementForm';
import AdvertisementDetails from './AdvertisementDetails';

const { Search } = Input;
const { Option } = Select;

const AdvertisementsList: React.FC = () => {
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState<AdvertisementStatistics | null>(null);
  const [filters, setFilters] = useState<AdvertisementFilters>({});
  
  // Modal states
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [editingAdvertisement, setEditingAdvertisement] = useState<Advertisement | null>(null);
  const [selectedAdvertisement, setSelectedAdvertisement] = useState<Advertisement | null>(null);

  useEffect(() => {
    loadAdvertisements();
    loadStatistics();
  }, [filters]);

  const loadAdvertisements = async () => {
    setLoading(true);
    try {
      const result = await advertisementAPI.getAdvertisements(filters);
      if (result.success) {
        setAdvertisements(result.data);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Reklamları yükləmə xətası');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const result = await advertisementAPI.getStatistics();
      if (result.success) {
        setStatistics(result.data);
      }
    } catch (error) {
      console.error('Statistikaları yükləmə xətası:', error);
    }
  };

  const handleSearch = (value: string) => {
    setFilters({ ...filters, search: value });
  };

  const handleFilterChange = (key: keyof AdvertisementFilters, value: string) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleCreate = () => {
    setEditingAdvertisement(null);
    setIsFormModalVisible(true);
  };

  const handleEdit = (advertisement: Advertisement) => {
    setEditingAdvertisement(advertisement);
    setIsFormModalVisible(true);
  };

  const handleView = (advertisement: Advertisement) => {
    setSelectedAdvertisement(advertisement);
    setIsDetailsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await advertisementAPI.deleteAdvertisement(id);
      if (result.success) {
        message.success(result.message);
        loadAdvertisements();
        loadStatistics();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Silmə xətası');
    }
  };

  const handleSend = async (advertisement: Advertisement) => {
    if (!advertisement.id) return;

    Modal.confirm({
      title: 'Reklamı göndər',
      content: `Bu reklamı ${advertisement.total_recipients} tələbəyə göndərmək istəyirsiniz? Ümumi ${advertisement.total_sms_count} SMS göndəriləcək.`,
      okText: 'Göndər',
      cancelText: 'Ləğv et',
      onOk: async () => {
        try {
          const result = await advertisementAPI.sendAdvertisement(advertisement.id!);
          if (result.success) {
            message.success(result.message);
            loadAdvertisements();
            loadStatistics();
          } else {
            message.error(result.message);
          }
        } catch (error) {
          message.error('Göndərmə xətası');
        }
      }
    });
  };

  const handleFormSuccess = () => {
    setIsFormModalVisible(false);
    loadAdvertisements();
    loadStatistics();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'hazırlanır': return 'blue';
      case 'göndərilir': return 'orange';
      case 'göndərilib': return 'green';
      case 'xəta': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'hazırlanır': return 'Hazırlanır';
      case 'göndərilir': return 'Göndərilir';
      case 'göndərilib': return 'Göndərilib';
      case 'xəta': return 'Xəta';
      default: return status;
    }
  };

  const getMessageTypeColor = (type: string) => {
    return type === 'reklam' ? 'purple' : 'cyan';
  };

  const columns = [
    {
      title: 'Başlıq',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Advertisement) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.created_by_name} • {new Date(record.created_at!).toLocaleDateString('az-AZ')}
          </div>
        </div>
      ),
    },
    {
      title: 'Növ',
      dataIndex: 'message_type',
      key: 'message_type',
      width: 100,
      render: (type: string) => (
        <Tag color={getMessageTypeColor(type)}>
          {type === 'reklam' ? 'Reklam' : 'İnformativ'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Alıcılar',
      dataIndex: 'total_recipients',
      key: 'total_recipients',
      width: 100,
      render: (count: number) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold' }}>{count}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>tələbə</div>
        </div>
      ),
    },
    {
      title: 'SMS',
      key: 'sms_info',
      width: 120,
      render: (record: Advertisement) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold' }}>{record.total_sms_count}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {record.sms_count_per_recipient} SMS/alıcı
          </div>
        </div>
      ),
    },
    {
      title: 'Uğur nisbəti',
      key: 'success_rate',
      width: 120,
      render: (record: Advertisement) => {
        if (record.status === 'hazırlanır') {
          return <span style={{ color: '#666' }}>-</span>;
        }
        
        const rate = record.total_recipients! > 0 
          ? (record.successful_sends! / record.total_recipients!) * 100 
          : 0;
        
        const color = rate > 80 ? '#52c41a' : rate > 50 ? '#faad14' : '#ff4d4f';
        
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color }}>{rate.toFixed(1)}%</div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {record.successful_sends}/{record.total_recipients}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      width: 200,
      render: (record: Advertisement) => (
        <Space size="small">
          <Tooltip title="Bax">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          {record.status === 'hazırlanır' && (
            <>
              <Tooltip title="Redaktə et">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              
              <Tooltip title="Göndər">
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  onClick={() => handleSend(record)}
                  disabled={!record.total_recipients || record.total_recipients === 0}
                />
              </Tooltip>
              
              <Popconfirm
                title="Bu reklamı silmək istəyirsiniz?"
                onConfirm={() => handleDelete(record.id!)}
                okText="Bəli"
                cancelText="Xeyr"
              >
                <Tooltip title="Sil">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Statistikalar */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Ümumi Reklamlar"
                value={statistics.total_advertisements}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Göndərilmiş"
                value={statistics.sent_advertisements}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Ümumi SMS"
                value={statistics.total_sms_sent}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Uğur nisbəti"
                value={statistics.success_rate}
                suffix="%"
                valueStyle={{ color: statistics.success_rate > 80 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filterlər və əməliyyatlar */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle">
              <Search
                placeholder="Başlıq və ya məzmun axtar..."
                allowClear
                onSearch={handleSearch}
                style={{ width: 300 }}
              />
              
              <Select
                placeholder="Mesaj növü"
                allowClear
                style={{ width: 150 }}
                onChange={(value) => handleFilterChange('message_type', value)}
              >
                <Option value="reklam">Reklam</Option>
                <Option value="informativ">İnformativ</Option>
              </Select>
              
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 150 }}
                onChange={(value) => handleFilterChange('status', value)}
              >
                <Option value="hazırlanır">Hazırlanır</Option>
                <Option value="göndərilir">Göndərilir</Option>
                <Option value="göndərilib">Göndərilib</Option>
                <Option value="xəta">Xəta</Option>
              </Select>
            </Space>
          </Col>
          
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadAdvertisements}
                loading={loading}
              >
                Yenilə
              </Button>
              
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Yeni Reklam
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Cədvəl */}
      <Card>
        <Table
          columns={columns}
          dataSource={advertisements}
          rowKey="id"
          loading={loading}
          pagination={{
            total: advertisements.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} reklam`,
          }}
        />
      </Card>

      {/* Form Modal */}
      <Modal
        title={editingAdvertisement ? 'Reklamı Redaktə Et' : 'Yeni Reklam'}
        open={isFormModalVisible}
        onCancel={() => setIsFormModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <AdvertisementForm
          advertisement={editingAdvertisement}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsFormModalVisible(false)}
        />
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Reklam Təfərrüatları"
        open={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        {selectedAdvertisement && (
          <AdvertisementDetails
            advertisement={selectedAdvertisement}
            onClose={() => setIsDetailsModalVisible(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default AdvertisementsList;